import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetcher } from "@/shared/api";
import { env } from "@/shared/config/env";
import { socialKeys } from "./queryKeys";
import {
  SOCIAL_MEDIA_PAGE_SIZE,
  type DisconnectPreview,
  type SocialConnection,
  type SocialMediaPage,
  type SocialPlatform,
} from "../model/types";

/**
 * Every social response rides the standard `{ success, message, data }`
 * envelope. Unwrapping defensively (rather than reaching straight for
 * `.data`) keeps these hooks working against a route that ever answers bare,
 * which some older merchant routes still do.
 */
function unwrap<T>(response: unknown): T {
  const body = response as { data?: T } | T;
  if (body && typeof body === "object" && "data" in (body as object)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

/**
 * Every query below is additionally gated on `env.SOCIAL_IMPORT`. With the
 * feature hidden no `/social/*` request is made at all — the gate lives here,
 * at the one place the requests are issued, rather than at each of the dozen
 * call sites that would otherwise each have to remember.
 */
function socialEnabled(enabled: boolean | undefined) {
  return env.SOCIAL_IMPORT && (enabled ?? true);
}

/**
 * Which platforms are connected. Always returns an entry for BOTH platforms,
 * so callers render one shape and never branch on a missing key.
 *
 * Permission: `store.read`.
 */
export function useSocialConnections(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: socialKeys.connections(),
    queryFn: () => fetcher<unknown>("/social/connections"),
    select: (response) => unwrap<SocialConnection[]>(response) ?? [],
    enabled: socialEnabled(options.enabled),
    staleTime: 30 * 1000,
  });
}

/** Pull one platform's entry out of the always-two-long list. */
export function useSocialConnection(
  platform: SocialPlatform,
  options: { enabled?: boolean } = {},
) {
  const query = useSocialConnections(options);
  const connection =
    query.data?.find((entry) => entry.platform === platform) ?? null;
  return { ...query, connection };
}

/**
 * The picker feed: one cursor-paged page of the connected account's own
 * posts. `nextCursor: null` means the end.
 *
 * A 409 here means not connected, or `REAUTH_REQUIRED` — deliberately not a
 * 404, so the caller can send the merchant to reconnect rather than tell them
 * something is missing. Not retried: retrying a 409 cannot fix it.
 *
 * Permission: `product.read`.
 */
export function useSocialMedia(
  platform: SocialPlatform,
  options: { enabled?: boolean; limit?: number } = {},
) {
  const limit = options.limit ?? SOCIAL_MEDIA_PAGE_SIZE;
  return useInfiniteQuery({
    queryKey: socialKeys.media(platform, limit),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (pageParam) params.set("cursor", String(pageParam));
      return fetcher<unknown>(`/social/${platform}/media?${params.toString()}`);
    },
    select: (result) => ({
      ...result,
      pages: result.pages.map((page) => unwrap<SocialMediaPage>(page)),
    }),
    getNextPageParam: (lastPage) =>
      unwrap<SocialMediaPage>(lastPage)?.nextCursor ?? undefined,
    enabled: socialEnabled(options.enabled),
    // The feed is a live account; a stale page shows posts that are gone.
    staleTime: 0,
    retry: false,
  });
}

/**
 * What a disconnect would delete. Read-only and safe to call repeatedly —
 * it exists to fill the confirmation dialog.
 *
 * `staleTime: 0` on purpose: the merchant can leave the dialog open while
 * another tab imports, and the figures in front of a destructive action
 * should be the freshest we can get.
 *
 * Permission: `store.update`.
 */
export function useDisconnectPreview(
  platform: SocialPlatform,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: socialKeys.disconnectPreview(platform),
    queryFn: () => fetcher<unknown>(`/social/${platform}/disconnect-preview`),
    select: (response) => unwrap<DisconnectPreview>(response),
    enabled: socialEnabled(options.enabled),
    staleTime: 0,
  });
}
